import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Leaf, Recycle, Globe, TreePine } from 'lucide-react';

const SustainabilityCounter = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.3 });
  const [counters, setCounters] = useState({
    bottles: 0,
    plastic: 0,
    trees: 0,
    water: 0
  });

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ["20px", "-20px"]);

  // Counter animation targets
  const targets = {
    bottles: 1000000,
    plastic: 85,
    trees: 5000,
    water: 500000
  };

  // Animate counters when in view
  useEffect(() => {
    if (isInView) {
      const duration = 2000; // 2 seconds
      const steps = 60;
      const stepDuration = duration / steps;

      const intervals = Object.keys(targets)?.map(key => {
        const target = targets?.[key];
        const increment = target / steps;
        let current = 0;
        let step = 0;

        return setInterval(() => {
          step++;
          current = Math.min(increment * step, target);
          
          setCounters(prev => ({
            ...prev,
            [key]: Math.floor(current)
          }));

          if (step >= steps) {
            clearInterval(intervals?.find(interval => interval === this));
          }
        }, stepDuration);
      });

      return () => {
        intervals?.forEach(interval => clearInterval(interval));
      };
    }
  }, [isInView]);

  // Animated flowing water lines
  const FlowingWaterLine = ({ delay = 0, direction = 1 }) => (
    <motion.div
      initial={{ pathLength: 0, opacity: 0 }}
      animate={isInView ? { pathLength: 1, opacity: 0.3 } : {}}
      transition={{ 
        duration: 3, 
        delay, 
        repeat: Infinity,
        repeatType: "loop",
        ease: "easeInOut"
      }}
      className="absolute inset-0"
    >
      <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        <motion.path
          d={direction > 0 ? "M0,50 Q25,25 50,50 T100,50" : "M100,50 Q75,75 50,50 T0,50"}
          stroke="url(#waterGradient)"
          strokeWidth="0.5"
          fill="none"
          animate={{
            strokeDasharray: ["0 100", "50 50", "100 0"],
            strokeDashoffset: [0, -50, -100]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "linear",
            delay
          }}
        />
      </svg>
    </motion.div>
  );

  // Eco Globe Animation Component
  const EcoGlobe = () => (
    <motion.div
      animate={{ 
        rotate: 360,
        scale: [1, 1.1, 1]
      }}
      transition={{ 
        rotate: { duration: 20, repeat: Infinity, ease: "linear" },
        scale: { duration: 4, repeat: Infinity, ease: "easeInOut" }
      }}
      className="absolute top-10 right-10 w-20 h-20 opacity-10"
    >
      <Globe className="w-full h-full text-mava-orange" />
      <motion.div
        animate={{ scale: [0.8, 1.2, 0.8] }}
        transition={{ duration: 3, repeat: Infinity }}
        className="absolute inset-2 bg-mava-gradient rounded-full opacity-20"
      />
    </motion.div>
  );

  const sustainabilityData = [
    {
      icon: Recycle,
      count: counters?.bottles,
      suffix: '+',
      label: 'Bottles Recycled',
      description: 'Responsible packaging and recycling initiatives',
      color: 'text-green-500',
      bgColor: 'bg-green-500'
    },
    {
      icon: Leaf,
      count: counters?.plastic,
      suffix: '%',
      label: 'Plastic Reduction',
      description: 'Reduced plastic usage in our packaging',
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500'
    },
    {
      icon: TreePine,
      count: counters?.trees,
      suffix: '+',
      label: 'Trees Planted',
      description: 'Environmental restoration and green initiatives',
      color: 'text-teal-500',
      bgColor: 'bg-teal-500'
    },
    {
      icon: Globe,
      count: counters?.water,
      suffix: 'L',
      label: 'Clean Water Sourced',
      description: 'Sustainable water sourcing and purification',
      color: 'text-blue-500',
      bgColor: 'bg-blue-500'
    }
  ];

  return (
    <motion.section
      ref={containerRef}
      style={{ y }}
      className="relative py-20 lg:py-32 bg-gradient-to-br from-green-50 to-blue-50 overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute inset-0">
        {/* Flowing water lines */}
        <FlowingWaterLine delay={0} direction={1} />
        <FlowingWaterLine delay={1} direction={-1} />
        <FlowingWaterLine delay={2} direction={1} />
        
        {/* Eco Globe */}
        <EcoGlobe />

        {/* Gradient definitions */}
        <svg className="absolute inset-0 w-0 h-0">
          <defs>
            <linearGradient id="waterGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#00C6FF" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#0072FF" stopOpacity="0.6" />
            </linearGradient>
          </defs>
        </svg>

        {/* Floating leaf particles */}
        {[...Array(8)]?.map((_, i) => (
          <motion.div
            key={i}
            animate={{ 
              y: [0, -20, 0],
              x: [0, Math.random() * 10 - 5, 0],
              rotate: [0, Math.random() * 20 - 10, 0]
            }}
            transition={{ 
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: "easeInOut"
            }}
            className={`absolute w-4 h-4 opacity-20`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`
            }}
          >
            🍃
          </motion.div>
        ))}
      </div>
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            animate={{ 
              scale: [1, 1.1, 1],
              rotate: [0, 10, -10, 0]
            }}
            transition={{ 
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-400 to-teal-500 rounded-full mb-6 liquid-shadow"
          >
            <Leaf className="w-8 h-8 text-white" />
          </motion.div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-poppins font-bold text-mava-charcoal mb-6">
            Sustainability
          </h2>
          <p className="text-xl font-inter text-gray-600 max-w-3xl mx-auto mb-4">
            Responsible packaging and clean water sourcing for a better tomorrow
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-green-400 to-teal-500 mx-auto rounded-full" />
        </motion.div>

        {/* Statistics Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
          {sustainabilityData?.map((item, index) => (
            <motion.div
              key={item?.label}
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="group"
            >
              <motion.div
                whileHover={{ scale: 1.05, y: -5 }}
                className="bg-white rounded-3xl p-8 text-center liquid-shadow brand-transition cursor-pointer h-full"
              >
                {/* Icon */}
                <motion.div
                  animate={{ 
                    rotate: [0, 10, -10, 0],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{ 
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: index * 0.5
                  }}
                  className={`inline-flex items-center justify-center w-16 h-16 ${item?.bgColor} rounded-full mb-6`}
                >
                  <item.icon className="w-8 h-8 text-white" />
                </motion.div>

                {/* Counter */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={isInView ? { scale: 1 } : {}}
                  transition={{ type: "spring", stiffness: 200, delay: 0.5 + index * 0.1 }}
                  className="mb-4"
                >
                  <span className={`text-4xl md:text-5xl font-poppins font-bold ${item?.color}`}>
                    {item?.count?.toLocaleString()}
                  </span>
                  <span className={`text-2xl font-poppins font-medium ${item?.color} ml-1`}>
                    {item?.suffix}
                  </span>
                </motion.div>

                {/* Label */}
                <h3 className="text-xl font-poppins font-bold text-mava-charcoal mb-3">
                  {item?.label}
                </h3>

                {/* Description */}
                <p className="text-gray-600 font-inter leading-relaxed">
                  {item?.description}
                </p>

                {/* Hover effect indicator */}
                <motion.div
                  initial={{ width: 0 }}
                  whileHover={{ width: "100%" }}
                  className={`h-1 ${item?.bgColor} rounded-full mx-auto mt-4 opacity-0 group-hover:opacity-100 brand-transition`}
                />
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1 }}
          className="text-center mt-16"
        >
          <p className="text-lg font-inter text-gray-700 mb-6 max-w-2xl mx-auto">
            Every bottle you choose makes a difference. Join us in our mission to create 
            a sustainable future while enjoying pure, refreshing beverages.
          </p>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-gradient-to-r from-green-400 to-teal-500 text-white px-8 py-4 rounded-full font-poppins font-semibold cursor-glow brand-transition"
          >
            Learn More About Our Impact
          </motion.button>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default SustainabilityCounter;